// Package imports:
import React, { useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
// Component imports:
import Input from '../../../ui-elements/Input/Formik/Input';
import Button from '../../../ui-elements/Button/Button';
import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
import Alert from '../../../ui-elements/Alert/Alert';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { Fetched } from '../../../types/Types';
import { IKeldanApiContact } from '../../../types/KeldanTypes';

const REASON_QUESTION = 'Almenn fyrirspurn á Keldunni';
const REASON_AD_REQUEST = 'Fyrirspurn um að auglýsa á Keldunni';
const REASON_INVOICE = 'Fyrirspurn um reikning frá Keldunni';
const REASON_KELDANAPP = 'Fyrirspurn um Keldan App';
const REASON_KELDANAPI = 'Fyrirspurn um Vefþjónustu Keldunnar';
// const REASON_KELDANMOREACCOUNTS = 'Fyrirspurn um fleiri notendur í áskrift';

const REASONS = [{
    label: REASON_QUESTION,
    value: REASON_QUESTION
},{
    label: REASON_AD_REQUEST,
    value: REASON_AD_REQUEST
},{
    label: REASON_INVOICE,
    value: REASON_INVOICE
},{
    label: REASON_KELDANAPP,
    value: REASON_KELDANAPP
},{
    label: REASON_KELDANAPI,
    value: REASON_KELDANAPI
// },{
//     label: REASON_KELDANMOREACCOUNTS,
//     value: REASON_KELDANMOREACCOUNTS
}];

interface IProps {
    selectedTopic?: number,
}

const HafaSambandContactForm: React.FC<IProps> = ({
    selectedTopic
}) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [topic, setTopic] = useState(
        (selectedTopic)
        // check if selectedTopic is between 0-[length-1] so we dont index out of array and crash
        ? (selectedTopic >= 0 && selectedTopic <= (REASONS.length-1))
            ? REASONS[selectedTopic].value
            : REASONS[0].value
        : REASONS[0].value);
    const [contactResponse, setContactResponse] = useState<Fetched<string>>(null);

    const handleSubmit = async (values: IFormValues) => {
        setLoading(true)
        try {
            setContactResponse(null);
            const requestQuery = new URLSearchParams({
                name: values.name,
                email: values.email,
                company: values.company,
                phone: values.phone,
                topic: topic,
                subject: values.message
            });
            const url = `${GET_KELDAN_API_URL()}/Hafa-Samband`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (response.ok) {
                const responseBody: IKeldanApiContact = await response.json();
                setLoading(false) 
                if (responseBody.success) {
                    setContactResponse(responseBody?.message || 'Fyrirspurn móttekin.');
                } else {
                    const { errors, message } = responseBody;
                    if (errors && errors.length > 0) {
                        setContactResponse(new Error(errors.join('\n')));
                    } else if (message) {
                        setContactResponse(new Error(message));
                    } else {
                        setContactResponse(new Error(ErrorMessages.ErrorInRequest));
                    }
                }
            } else {
                setLoading(false);
                setContactResponse(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            setLoading(false);
            setContactResponse(new Error(ErrorMessages.NetworkError));
        }
    }

    const subFormComponent = useMemo(() => {
        return ((props: { values: IFormValues }) => (
            <SubFormComponent
                topic={topic}
                setTopic={setTopic}
                contactResponse={contactResponse}
                values={props.values}
                loading={loading}
            />
        ))
    }, [ topic, contactResponse, loading ]);

    return (
        <div className='KCL_contact-form'>
            <Formik
                initialValues={{
                    name: '',
                    email: '',
                    company: '',
                    phone: '',
                    message: '',
                }}
                validationSchema={Yup.object({
                    name: Yup.string()
                        .required('Nafn verður að fylgja'),
                    email: Yup.string()
                        .email('Ógilt netfang').required('Netfang verður að fylgja'),
                    company: (topic === REASON_AD_REQUEST)
                        ? Yup.string()
                            .required('Fyrirtæki má ekki vanta í fyrirspurn um auglýsingar')
                        : Yup.string(),
                    phone: Yup.string()
                        .required('Símanúmer verður að fylgja')
                        .matches(/^[+]?\d+$/, 'Símanúmer má einungis innihalda tölustafi')
                        .min(7, 'Lágmarkslengd eru 7 tölustafir')
                        .max(11, 'Hámarkslengd eru 11 tölustafir'),
                    message: Yup.string()
                        .required("Skilaboð verða að fylgja"),
                })}
                onSubmit={handleSubmit}
                component={subFormComponent}
            />
        </div>
    );
}

export default HafaSambandContactForm;

interface IFormValues {
    name: string,
    email: string,
    company: string,
    phone: string,
    message: string,
}


interface ISubFormProps {
    topic: string,
    setTopic(topic: string): void;
    contactResponse: Fetched<string>
    values: IFormValues,
    loading: boolean
}

const SubFormComponent: React.FC<ISubFormProps> = ({
    topic,
    setTopic,
    contactResponse,
    values,
    loading
}) => {
        return (
            <Form>
                <div className="form__body">
                    <div className="form__section">
                        <h3>Þínar upplýsingar</h3>
                        <div className="form__row">
                            <div className="form__col">
                                <Input 
                                    label="Nafn"
                                    name="name"
                                    id="name"
                                    value={values.name}
                                />
                            </div>
                            <div className="form__col">
                                <Input
                                    label="Netfang"
                                    name="email"
                                    id="email"
                                    value={values.email}
                                />
                            </div>
                        </div>
                        <div className="form__row">
                            <div className="form__col">
                                <Input 
                                    label="Fyrirtæki"
                                    name="company"
                                    id="company"
                                    value={values.company}
                                />
                            </div>
                            <div className="form__col">
                                <Input
                                    label="Sími"
                                    name="phone"
                                    id="phone"
                                    value={values.phone}
                                />
                            </div>
                        </div>
                        <h3>Hvað getum við aðstoðað þig með?</h3>
                        <div className="form__row">
                            <div className="form__col">
                                {/* ADD DROPSELECT IMPLEMENTATION TO FORMIK */}
                                <DropSelect
                                    options={REASONS}
                                    defaultValue={topic}
                                    onChange={(option) => {
                                        if (typeof option === 'string') setTopic(option);
                                    }}
                                    size='lg'
                                />
                            </div>
                        </div>
                        <div className="form__row">
                            <div className="form__col">
                                <Input
                                    inputType="textArea"
                                    label="Skilaboð"
                                    name="message"
                                    id="message"
                                    value={values.message}
                                />
                            </div>
                        </div>
                        {(contactResponse === null)
                            ? null
                            : (
                                <div className="form__row">
                                    <div className="form__col">
                                        {(contactResponse instanceof Error)
                                        ? <Alert type='error' headText={contactResponse.message} />
                                        : <Alert type='info' headText={contactResponse} />}
                                    </div>
                                </div>
                            )}
                        <div className="form__actions">
                            <Button showLoader={loading} buttonType="primary" size="lg">Senda</Button>
                        </div>
                    </div>
                </div>
            </Form>
        );
    }
